import { useCallback, useEffect, useState } from "react";

import { useRuntimeConfig } from "../config/useRuntimeConfig";
import {
  associateSoftwareToken,
  buildTotpUri,
  changePassword as cognitoChangePassword,
  CognitoOperationError,
  getMfaState,
  setSoftwareTokenMfaEnabled,
  verifySoftwareToken,
  type CognitoMfaState,
} from "./cognitoIdp";
import { useAuth } from "./useAuth";

export type MfaPhase = "idle" | "loading" | "ready" | "error";

export type TotpEnrolment = {
  secret: string;
  otpauthUri: string;
};

const GENERIC_ERROR = "Something went wrong. Please try again.";

function messageFor(error: unknown): string {
  if (error instanceof CognitoOperationError) {
    return error.message;
  }
  return GENERIC_ERROR;
}

// Signed-in password + TOTP MFA operations. Every call goes straight to Cognito with the user's
// access token; nothing here is persisted and no password or secret passes through a MedoPilot
// backend. Cognito stays authoritative for the password policy and MFA state.
export function useCognitoSecurity() {
  const auth = useAuth();
  const config = useRuntimeConfig();
  const region = config?.region;
  const issuer = "MedoPilot";
  const accountName =
    (typeof (auth.user?.profile as Record<string, unknown> | undefined)?.email === "string"
      ? ((auth.user?.profile as Record<string, string>).email)
      : undefined) ?? "MedoPilot account";

  const [mfaState, setMfaState] = useState<CognitoMfaState | null>(null);
  const [mfaPhase, setMfaPhase] = useState<MfaPhase>("idle");
  const [mfaLoadError, setMfaLoadError] = useState<string | null>(null);

  const accessToken = auth.user?.access_token;

  const refreshMfa = useCallback(async () => {
    if (!accessToken) {
      return;
    }
    setMfaPhase("loading");
    setMfaLoadError(null);
    try {
      setMfaState(await getMfaState(region, accessToken));
      setMfaPhase("ready");
    } catch (error) {
      setMfaLoadError(messageFor(error));
      setMfaPhase("error");
    }
  }, [accessToken, region]);

  useEffect(() => {
    void refreshMfa();
  }, [refreshMfa]);

  const changePassword = useCallback(
    async (currentPassword: string, newPassword: string) => {
      if (!accessToken) {
        return "Your session has expired. Please sign in again.";
      }
      try {
        await cognitoChangePassword(region, accessToken, currentPassword, newPassword);
        return null;
      } catch (error) {
        return messageFor(error);
      }
    },
    [accessToken, region],
  );

  const beginTotpSetup = useCallback(async (): Promise<TotpEnrolment | { error: string }> => {
    if (!accessToken) {
      return { error: "Your session has expired. Please sign in again." };
    }
    try {
      const secret = await associateSoftwareToken(region, accessToken);
      return { secret, otpauthUri: buildTotpUri({ issuer, accountName, secret }) };
    } catch (error) {
      return { error: messageFor(error) };
    }
  }, [accessToken, accountName, region]);

  const confirmTotpSetup = useCallback(
    async (code: string) => {
      if (!accessToken) {
        return "Your session has expired. Please sign in again.";
      }
      try {
        await verifySoftwareToken(region, accessToken, code.replace(/\s+/g, ""), "MedoPilot");
        await setSoftwareTokenMfaEnabled(region, accessToken, true);
        await refreshMfa();
        return null;
      } catch (error) {
        return messageFor(error);
      }
    },
    [accessToken, refreshMfa, region],
  );

  const disableTotp = useCallback(async () => {
    if (!accessToken) {
      return "Your session has expired. Please sign in again.";
    }
    try {
      await setSoftwareTokenMfaEnabled(region, accessToken, false);
      await refreshMfa();
      return null;
    } catch (error) {
      return messageFor(error);
    }
  }, [accessToken, refreshMfa, region]);

  return {
    mfaState,
    mfaPhase,
    mfaLoadError,
    refreshMfa,
    changePassword,
    beginTotpSetup,
    confirmTotpSetup,
    disableTotp,
  };
}
